/**
 * Reactive Vue composable wrapping Supabase Auth.
 *
 * Provides login/logout actions, session tracking, and user state as
 * Vue reactive refs. Downstream stores and components should use this
 * composable instead of calling supabase.auth directly.
 */

import { supabase } from '@/lib/supabase'
import type { AuthChangeEvent, Provider, Session, User } from '@supabase/supabase-js'
import { onMounted, onUnmounted, readonly, ref, type Ref } from 'vue'

export interface UseSupabaseReturn {
    /** Current Supabase auth session (null when logged out) */
    session: Readonly<Ref<Session | null>>
    /** Current authenticated user (null when logged out) */
    user: Readonly<Ref<User | null>>
    /** Whether an auth operation is in progress */
    loading: Readonly<Ref<boolean>>
    /** Most recent auth error message (null when no error) */
    error: Readonly<Ref<string | null>>

    /** Sign in with email + password */
    signInWithPassword: (email: string, password: string) => Promise<void>
    /** Sign in via third-party OAuth provider (Google, GitHub, etc.) */
    signInWithOAuth: (provider: Provider) => Promise<void>
    /** Send a passwordless magic-link / OTP email */
    signInWithOtp: (email: string) => Promise<void>
    /** Verify a 6-digit OTP code sent via email */
    verifyOtp: (email: string, token: string) => Promise<void>
    /** Sign out and clear session */
    signOut: () => Promise<void>
    /** Get the current session (re-fetched from Supabase) */
    getSession: () => Promise<Session | null>
    /** Get the current user (re-fetched from Supabase) */
    getUser: () => Promise<User | null>
}

/**
 * Composable for Supabase authentication.
 *
 * Sets up an onAuthStateChange listener that keeps `session` and `user`
 * refs in sync automatically. The listener is cleaned up when the
 * component unmounts.
 */
export function useSupabase(): UseSupabaseReturn {
    const session = ref<Session | null>(null)
    const user = ref<User | null>(null)
    const loading = ref(false)
    const error = ref<string | null>(null)

    let authSubscription: { unsubscribe: () => void } | null = null

    function handleAuthChange(_event: AuthChangeEvent, newSession: Session | null) {
        session.value = newSession
        user.value = newSession?.user ?? null
    }

    onMounted(async () => {
        // Hydrate initial session
        const { data } = await supabase.auth.getSession()
        session.value = data.session
        user.value = data.session?.user ?? null

        // Subscribe to future auth changes
        const { data: sub } = supabase.auth.onAuthStateChange(handleAuthChange)
        authSubscription = sub.subscription
    })

    onUnmounted(() => {
        authSubscription?.unsubscribe()
    })

    async function signInWithPassword(email: string, password: string): Promise<void> {
        loading.value = true
        error.value = null
        try {
            const { error: authError } = await supabase.auth.signInWithPassword({
                email,
                password,
            })
            if (authError) {
                error.value = authError.message
            }
        } catch (e) {
            error.value = e instanceof Error ? e.message : 'Sign-in failed'
        } finally {
            loading.value = false
        }
    }

    async function signInWithOAuth(provider: Provider): Promise<void> {
        loading.value = true
        error.value = null
        try {
            const { error: authError } = await supabase.auth.signInWithOAuth({
                provider,
                options: {
                    redirectTo: window.location.origin,
                },
            })
            if (authError) {
                error.value = authError.message
            }
        } catch (e) {
            error.value = e instanceof Error ? e.message : 'OAuth sign-in failed'
        } finally {
            loading.value = false
        }
    }

    async function signInWithOtp(email: string): Promise<void> {
        loading.value = true
        error.value = null

        try {
            const { error: authError } = await supabase.auth.signInWithOtp({
                email,
                options: { shouldCreateUser: true }
            })

            if (authError) {
                error.value = authError.message
            }
        } catch (e) {
            error.value = e instanceof Error ? e.message : 'OTP sign-in failed'
        } finally {
            loading.value = false
        }
    }

    async function signOut(): Promise<void> {
        loading.value = true
        error.value = null
        try {
            const { error: authError } = await supabase.auth.signOut()
            if (authError) {
                error.value = authError.message
            }
        } catch (e) {
            error.value = e instanceof Error ? e.message : 'Sign-out failed'
        } finally {
            loading.value = false
        }
    }

    async function verifyOtp(email: string, token: string): Promise<void> {
        loading.value = true
        error.value = null
        try {
            const { error: authError } = await supabase.auth.verifyOtp({
                email,
                token,
                type: 'magiclink'
            })

            if (authError) {
                error.value = authError.message
            }
        } catch (e) {
            error.value = e instanceof Error ? e.message : 'OTP verification failed'
        } finally {
            loading.value = false
        }
    }

    async function getSession(): Promise<Session | null> {
        const { data } = await supabase.auth.getSession()
        return data.session
    }

    async function getUser(): Promise<User | null> {
        const { data } = await supabase.auth.getUser()
        return data.user
    }

    return {
        session: readonly(session) as Readonly<Ref<Session | null>>,
        user: readonly(user) as Readonly<Ref<User | null>>,
        loading: readonly(loading) as Readonly<Ref<boolean>>,
        error: readonly(error) as Readonly<Ref<string | null>>,
        signInWithPassword,
        signInWithOAuth,
        signInWithOtp,
        verifyOtp,
        signOut,
        getSession,
        getUser,
    }
}
